import type { FC } from 'react';

import type { TPickupDelivery } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import {
  Divider,
  Icon,
  Typography
} from '@/modules/perseo-billing/ui/react/src/components/beta';
import { getDay, getMonth } from '@/modules/perseo-billing/utils';

import SummaryQuantity from './summary-quantity.component';

type TDeliverySummary = {
  pickup?: TPickupDelivery;
  quantity: number;
  totalDeliveries: number;
  hasDifferentStores: boolean;
  isExpanded: boolean;
};

type TPickupDeliveryDetail = {
  pickup: TPickupDelivery;
  index: number;
  totalDeliveries: number;
};

const ICON_PROPS = {
  className:
    'co-fill-pds-information-500 co-text-pds-information-500 co-shrink-0',
  size: { width: 24, height: 24 }
};

const formatDataLabel = (pickup: TPickupDelivery) => {
  const deliveryDateString = pickup?.deliveryDateString;

  let dateLabel = '';
  const month = getMonth({
    dateString: deliveryDateString,
    responseFormat: 'short'
  });
  const day = getDay({
    dateString: deliveryDateString,
    responseFormat: 'numeric'
  });
  dateLabel = `desde el ${day} de ${month}`;
  return dateLabel;
};

const PickupDeliverySummary: FC<TDeliverySummary> = ({
  pickup,
  totalDeliveries,
  quantity,
  hasDifferentStores,
  isExpanded
}) => {
  const proxyPickup = pickup?.proxyPickup;
  const recipientFullName = pickup?.recipientFullName;
  const storeName = pickup?.pickupStoreName;
  const streetName = pickup?.pickupStoreStreetName;
  const streetNumber = pickup?.pickupStoreStreetNumber;
  const commune = pickup?.pickupStoreCommune;

  const address = `${streetName} ${streetNumber}, ${commune}`;

  const iWillPickUp = 'retiraré mi producto.';
  const thirdWillPickUp = 'retirará mi producto.';

  const userLabel = proxyPickup ? recipientFullName : 'Yo';
  const pickupLabel = proxyPickup ? thirdWillPickUp : iWillPickUp;

  return (
    <div
      className="co-w-full co-h-full co-flex co-items-stretch co-gap-8px lg:co-p-8px co-bg-white lg:co-bg-pds-neutral-100 co-rounded-4px"
      tabIndex={0}
    >
      {totalDeliveries >= 2 && (
        <>
          <SummaryQuantity quantity={quantity} />
          <span className="co-w-1 co-min-h-[52px] co-h-[52px] co-border-l-1 co-border-[#E6E6E6]"></span>
        </>
      )}
      <div className="co-w-full co-flex co-flex-col co-gap-8px co-overflow-hidden">
        <PickupSummaryHeader
          pickup={pickup}
          hasDifferentStores={hasDifferentStores}
        />

        {!hasDifferentStores && (
          <>
            {totalDeliveries < 2 && <Divider />}
            <p className="co-text-body1 co-text-neutral-grey-500 co-leading-tight">
              {isExpanded && (
                <span className="co-text-body1 co-font-semibold co-text-neutral-grey-500">
                  {storeName}
                  {': '}
                </span>
              )}
              {address}
            </p>
            {isExpanded && (
              <p className="co-text-body1 co-text-neutral-grey-500 co-leading-tight">
                <span className="co-text-body1 co-font-semibold co-text-neutral-grey-500">
                  {userLabel}{' '}
                </span>
                {pickupLabel}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

type PickupSummaryHeaderProps = {
  pickup?: TPickupDelivery;
  hasDifferentStores: boolean;
};

const PickupSummaryHeader = ({
  pickup,
  hasDifferentStores
}: PickupSummaryHeaderProps) => {
  const storeName = pickup?.pickupStoreName;
  return (
    <div className="co-flex co-items-center co-gap-4px co-overflow-hidden">
      <Icon icon="StoreBilling" {...ICON_PROPS} />
      <Typography
        className="co-text-pds-information-600 co-whitespace-nowrap"
        weight="semibold"
        leading="tight"
      >
        Retira en
      </Typography>
      <Typography
        className="co-text-neutral-grey-500 co-whitespace-nowrap"
        weight="semibold"
        leading="tight"
        noWrap
      >
        {hasDifferentStores || !storeName ? 'tienda' : storeName}
      </Typography>
    </div>
  );
};

const PickupDeliveryDetail: FC<TPickupDeliveryDetail> = ({
  pickup,
  index,
  totalDeliveries
}) => {
  const proxyPickup = pickup?.proxyPickup;
  const recipientFullName = pickup?.recipientFullName;
  const storeName = pickup?.pickupStoreName;
  const streetName = pickup?.pickupStoreStreetName;
  const streetNumber = pickup?.pickupStoreStreetNumber;
  const commune = pickup?.pickupStoreCommune;

  const address = `${streetName} ${streetNumber}, ${commune}`;

  const iWillPickUp = 'retiraré mi producto.';
  const thirdWillPickUp = 'retirará mi producto.';

  const userLabel = proxyPickup ? recipientFullName : 'Yo';
  const pickupLabel = proxyPickup ? thirdWillPickUp : iWillPickUp;

  const dateLabel = formatDataLabel(pickup);

  return (
    <div
      className="co-w-full co-h-full co-flex co-items-center co-gap-8px lg:co-p-8px co-bg-white lg:co-bg-pds-neutral-100 co-rounded-4px"
      key={index}
      tabIndex={0}
    >
      <div className="co-w-full co-flex co-flex-col co-gap-8px">
        <div className="co-flex co-justify-between">
          {totalDeliveries > 1 && (
            <Typography weight="semibold" color="pds-neutral-700" size="body1">
              Entrega {index + 1}
            </Typography>
          )}
          <div className="co-flex co-items-center co-gap-4px">
            <Icon icon="StoreBilling" {...ICON_PROPS} />

            <>
              <Typography
                color="pds-information-600"
                weight="semibold"
                leading="tight"
                noWrap
              >
                Retira
              </Typography>
              <Typography
                color="neutral-grey-500"
                weight="semibold"
                leading="tight"
              >
                {dateLabel}
              </Typography>
            </>
          </div>
        </div>
        <Divider />
        <p className="co-text-body1 co-text-neutral-grey-500 co-leading-tight">
          <span className="co-text-body1 co-font-semibold co-text-neutral-grey-500">
            {storeName ? storeName + ': ' : ' '}
          </span>
          {address}
        </p>
        <p className="co-text-body1 co-text-neutral-grey-500 co-leading-tight">
          <span className="co-text-body1 co-font-semibold co-text-neutral-grey-500">
            {userLabel}{' '}
          </span>{' '}
          {pickupLabel}
        </p>
      </div>
    </div>
  );
};

export { PickupDeliveryDetail, PickupDeliverySummary };
